import { memo, useCallback } from 'react'

import { Cancel } from '@mui/icons-material'
import { TextField } from '@mui/material'
import { useSnackbar } from 'notistack'
import { Controller, useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { useShallow } from 'zustand/react/shallow'

import { useCloseHandler } from '@shared/components/MaterialUIEnhancements/Dialog'
import { SnackPanelActions, ViewSpace } from '@shared/components/Snackbar'
import {
  selectSubmissionProgressSetters,
  SubmissionButton,
  SubmissionProgress,
  useSubmissionProgressStore,
} from '@shared/components/SubmissionProgress'
import { DialogFormContent } from '@shared/layouts'
import { SnackPanelActionButtonLink } from 'components/LinkedMUIComponents'
import { useWorkspaceCreateMutation } from 'hooks/mutations/smep'
import { workspacesOverviewRoute } from 'pages/NWPlatform/Workspaces/Routing'
import { workspaceRoute } from 'pages/NWPlatform/Workspaces/Workspace'

type FormStructure = {
  name: string
}

const WorkspaceDialogContentRaw = (): JSX.Element => {
  const { t } = useTranslation(['common', 'domain', 'entity'])
  const { enqueueSnackbar } = useSnackbar()
  const handleClose = useCloseHandler()

  const { organizationId, environmentId } = workspacesOverviewRoute.useParams()

  const { setCurrentStep } = useSubmissionProgressStore(useShallow(selectSubmissionProgressSetters))

  const { handleSubmit, control, formState } = useForm<FormStructure>()

  const workspaceCreateMutation = useWorkspaceCreateMutation()
  const { mutate: createWorkspace } = workspaceCreateMutation

  const handleFormSubmit = useCallback(
    (data: FormStructure) => {
      setCurrentStep({ value: 10, description: t('domain:Workspace.creatingWorkspace') })

      createWorkspace(
        {
          smepWorkspaceInsert: { ...data, environmentId },
        },
        {
          onSuccess: (data) => {
            setCurrentStep({
              value: 100,
              description: t('created', { name: data.name }),
            })

            setTimeout(() => {
              enqueueSnackbar(`${t('created', { name: data.name })}`, {
                description: `${t('domain:Workspace.createdDescription')}`,
                variant: 'panel',
                persist: true,
                icon: 'success',
                Actions: ({ onClose }) => (
                  <SnackPanelActions>
                    <SnackPanelActionButtonLink
                      to={workspaceRoute.to}
                      params={{ organizationId, environmentId, workspaceId: data.id }}
                      onClick={() => onClose()}
                    >
                      <ViewSpace />
                    </SnackPanelActionButtonLink>
                  </SnackPanelActions>
                ),
              })

              handleClose()
            }, 700)
          },
          onError: () => {
            setCurrentStep({
              value: 90,
              description: t('domain:Workspace.somethingWentWrong'),
              icon: <Cancel fontSize="small" color="error" />,
            })
          },
        },
      )
    },
    [createWorkspace, enqueueSnackbar, environmentId, handleClose, organizationId, setCurrentStep, t],
  )

  return (
    <DialogFormContent
      onSubmit={handleSubmit(handleFormSubmit)}
      title={t('domain:Workspace.manageWorkspace')}
      description={t('domain:Workspace.workspaceExplanation')}
      SaveButtonProps={{
        disabled: formState.isSubmitSuccessful,
      }}
      SubmitProgressZone={
        <SubmissionProgress isSuccess={workspaceCreateMutation.isSuccess} isError={workspaceCreateMutation.isError} />
      }
      SaveButton={
        <SubmissionButton
          submitCondition={!workspaceCreateMutation.isSuccess && !workspaceCreateMutation.isError}
          successCondition={workspaceCreateMutation.isSuccess}
          errorCondition={workspaceCreateMutation.isError && !workspaceCreateMutation.isSuccess}
          loading={workspaceCreateMutation.isPending}
          disabled={(formState.isSubmitted && !formState.isValid) || workspaceCreateMutation.isSuccess}
        />
      }
    >
      <Controller
        name="name"
        control={control}
        rules={{
          required: true,
        }}
        render={({ field, fieldState }) => (
          <TextField
            sx={{ mt: 2 }}
            {...field}
            label={t('name')}
            required
            fullWidth
            helperText={fieldState.error?.message}
            error={!!fieldState.error}
          />
        )}
      />
    </DialogFormContent>
  )
}

export const WorkspaceDialogContent = memo(WorkspaceDialogContentRaw)
